/**
 * ホームページのヘッダー要素（ロゴとハンバーガーメニュー）のスクロールアニメーションを制御するモジュール
 * 75vhスクロールすると、top: -25% から指定値に切り替わる
 * PC（1025px以上）: pxvwVal(52) = min(3.611vw, 52px)
 * SP（1024px以下）: vw(32, 393) = 8.142vw
 *
 * SP時のみ、フッターが画面上部に到達するとheader__logoがスクロールに追従して上に移動する
 * SP時のみ、フッターが画面に見えたらheader__hamが非表示になる
 * SP時のみ、8.142vwの位置に来たらheader__logoに.visibleクラスを付与する
 * SP時のみ、translate/scaleの制御は全ページの.header__logoに適用する
 *
 * @example
 * // 使用例
 * import { initHomeHeaderScroll } from './module/page-home_header';
 * initHomeHeaderScroll();
 */

/** PCのブレイクポイント */
const PC_BREAKPOINT = 1025;

/** SP時のtop値をpxで取得する (156/393*100)vw */
const getSpTopPx = (): number => (156 / 393) * window.innerWidth;

/** SPかどうかを判定する */
const isSp = (): boolean => window.innerWidth < PC_BREAKPOINT;

/** 診断ページかどうかを判定する（ロゴはCSSで絶対配置するためJS制御の対象外） */
const isCheckPage = (): boolean => !!document.querySelector('.check__inner');

/**
 * ホームページのヘッダースクロールアニメーションを初期化する
 *
 * @returns {void}
 *
 * @example
 * // ページ読み込み時に初期化
 * document.addEventListener('DOMContentLoaded', () => {
 *   initHomeHeaderScroll();
 * });
 */
export const initHomeHeaderScroll = (): void => {
  /** topの制御対象（homeページのロゴのみ） */
  const headerLogo = document.querySelector<HTMLElement>('.home .header__logo');
  /** translate/scaleの制御対象（全ページのロゴ） */
  const headerLogos = document.querySelectorAll<HTMLElement>('.header__logo');
  const headerHam = document.querySelector<HTMLElement>('.header__ham.fixed');
  const footer = document.querySelector<HTMLElement>('.footer');

  // headerLogosとfooterだけあれば最低限動く
  if (headerLogos.length === 0 || !footer) return;

  /** 診断ページはロゴの追従制御を行わない */
  const skipLogoFollow = isCheckPage();

  /** ハンバーガーメニューがフッターで非表示になっているか */
  let isHamHidden = false;

  /** ページ遷移直後（scrollY === 0）かどうかのフラグ */
  let isInitial = true;

  /** transition: none を適用する対象要素リスト */
  const transitionTargets = [
    ...Array.from(headerLogos),
    ...(headerHam ? [headerHam] : []),
    ...(headerLogo ? [headerLogo] : []),
  ];

  /**
   * スクロール位置に基づいてヘッダー要素のtop位置を更新する
   * topの制御は .home .header__logo のみ
   */
  const updateHeaderPosition = (): void => {
    const isPc = window.innerWidth >= PC_BREAKPOINT;

    // visibleクラスの制御は全ページのheader__logoに適用
    if (skipLogoFollow) {
      headerLogos.forEach((logo) => logo.classList.remove('visible'));
    } else if (!isPc) {
      if (window.scrollY >= window.innerHeight * 0.75 + window.innerHeight) {
        headerLogos.forEach((logo) => logo.classList.add('visible'));
      } else {
        headerLogos.forEach((logo) => logo.classList.remove('visible'));
      }
    } else {
      headerLogos.forEach((logo) => logo.classList.remove('visible'));
    }

    // hamのtop制御は全ページに適用
    if (headerHam && !isHamHidden) {
      const hamTargetVw = isPc ? 'min(3.611vw, 52px)' : '8.142vw';
      const hamTopValue = window.scrollY >= window.innerHeight * 0.75 ? hamTargetVw : '-25vw';
      headerHam.style.top = hamTopValue;
    }

    // top制御はhomeページのみ
    if (!headerLogo) return;

    const targetVw = isPc ? 'min(3.611vw, 52px)' : '8.142vw';
    const topValue = window.scrollY >= window.innerHeight * 0.75 ? targetVw : '-25vw';
    headerLogo.style.top = topValue;
  };

  /**
   * SP時のフッター到達によるtranslate/scale制御
   * 全ページの.header__logoに適用する
   */
  const updateLogoTransform = (): void => {
    if (skipLogoFollow) return;

    if (!isSp()) {
      headerLogos.forEach((logo) => {
        logo.style.translate = '-50% 0';
        logo.style.scale = '';
      });
      return;
    }

    const footerRect = footer.getBoundingClientRect();
    const spTopPx = getSpTopPx();

    if (footerRect.top >= window.innerHeight * 0.5) {
      headerLogos.forEach((logo) => {
        logo.style.translate = '-50% 0';
        logo.style.scale = '';
      });
      return;
    }

    const translateYValue = Math.max(0, spTopPx - footerRect.top);
    headerLogos.forEach((logo) => {
      logo.style.translate = `-50% -${translateYValue}px`;
      logo.style.scale = '1.2';
    });
  };

  /**
   * SP時のフッター表示によるハンバーガーメニュー非表示制御
   */
  const updateHamVisibility = (): void => {
    if (!headerHam) return;

    if (!isSp()) {
      isHamHidden = false;
      return;
    }

    const footerRect = footer.getBoundingClientRect();

    if (footerRect.top < window.innerHeight) {
      headerHam.style.top = '-25vw';
      isHamHidden = true;
    } else {
      isHamHidden = false;
    }
  };

  /**
   * スクロール時の処理
   */
  const onScroll = (): void => {
    // 初回スクロール時にtransition: noneを解除する
    if (isInitial) {
      isInitial = false;
      transitionTargets.forEach((el) => (el.style.transition = ''));
    }
    updateHamVisibility();
    updateHeaderPosition();
    updateLogoTransform();
  };

  /**
   * リサイズ時の処理
   */
  const onResize = (): void => {
    updateHamVisibility();
    updateHeaderPosition();
    updateLogoTransform();
  };

  window.addEventListener('scroll', onScroll, { passive: true });
  window.addEventListener('resize', onResize, { passive: true });

  // 初期配置はtransition: noneで行う（ページ遷移時のアニメーション防止）
  transitionTargets.forEach((el) => (el.style.transition = 'none'));

  updateHamVisibility();
  updateHeaderPosition();
  updateLogoTransform();
};

document.addEventListener('DOMContentLoaded', () => {
  initHomeHeaderScroll();
});